import { useState, FormEvent } from "react";

export default function CTASection() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [business, setBusiness] = useState("");
  const [message, setMessage] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (message.length > 500) return;
    setLoading(true);

    const body = new URLSearchParams({
      name,
      email,
      business_type: business,
      message,
    }).toString();

    try {
      await fetch("https://readdy.ai/api/form/d6sotbah642up23iq180", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body,
      });
      setSubmitted(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="get-started" className="w-full bg-[#09090B] py-28 px-6 border-t border-white/5">
      <div className="max-w-7xl mx-auto">
        {/* Background glow */}
        <div className="relative rounded-3xl overflow-hidden border border-white/8 bg-[#0F1115]">
          {/* Radial gradient */}
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background: "radial-gradient(ellipse at 50% 0%, rgba(74,222,128,0.08) 0%, transparent 60%)",
            }}
          />

          <div className="relative z-10 grid grid-cols-1 md:grid-cols-2 gap-12 p-12 md:p-16">
            {/* Left: Copy */}
            <div className="flex flex-col justify-center">
              <div className="inline-block text-xs font-semibold uppercase tracking-widest text-[#4ADE80]/80 mb-4">
                Get Started
              </div>
              <h2
                className="text-4xl md:text-5xl font-bold text-white leading-tight mb-5"
                style={{ fontFamily: "'Space Grotesk', sans-serif" }}
              >
                Stop Operating on{" "}
                <span className="text-[#4ADE80]">Unstable Systems</span>
              </h2>
              <p className="text-white/50 text-lg leading-relaxed mb-8">
                Build a foundation that supports your growth—now and long-term.
              </p>

              {/* Trust signals */}
              <div className="space-y-3">
                {[
                  "Structured systems audit included",
                  "No long-term contracts required",
                  "Results-focused implementation",
                ].map((item) => (
                  <div key={item} className="flex items-center gap-3">
                    <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                      <i className="ri-checkbox-circle-fill text-[#4ADE80] text-base" />
                    </div>
                    <span className="text-sm text-white/50">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Right: Form */}
            <div>
              {submitted ? (
                <div className="h-full flex flex-col items-center justify-center text-center py-12">
                  <div className="w-14 h-14 flex items-center justify-center rounded-full bg-[#4ADE80]/10 border border-[#4ADE80]/30 mb-5">
                    <i className="ri-check-line text-[#4ADE80] text-2xl" />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                    You're on the list
                  </h3>
                  <p className="text-white/45 text-sm max-w-xs">
                    We'll review your information and reach out within 1 business day to schedule your audit.
                  </p>
                </div>
              ) : (
                <form
                  data-readdy-form
                  onSubmit={handleSubmit}
                  className="space-y-4"
                >
                  {/* Name */}
                  <div>
                    <label className="block text-xs font-medium text-white/40 mb-1.5 uppercase tracking-wider">
                      Full Name
                    </label>
                    <input
                      type="text"
                      name="name"
                      required
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="John Smith"
                      className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-sm text-white placeholder-white/20 focus:outline-none focus:border-[#4ADE80]/40 transition-colors"
                    />
                  </div>

                  {/* Email */}
                  <div>
                    <label className="block text-xs font-medium text-white/40 mb-1.5 uppercase tracking-wider">
                      Email Address
                    </label>
                    <input
                      type="email"
                      name="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="john@company.com"
                      className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-sm text-white placeholder-white/20 focus:outline-none focus:border-[#4ADE80]/40 transition-colors"
                    />
                  </div>

                  {/* Business Type */}
                  <div>
                    <label className="block text-xs font-medium text-white/40 mb-1.5 uppercase tracking-wider">
                      Business Type
                    </label>
                    <select
                      name="business_type"
                      required
                      value={business}
                      onChange={(e) => setBusiness(e.target.value)}
                      className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-sm text-white focus:outline-none focus:border-[#4ADE80]/40 transition-colors appearance-none"
                    >
                      <option value="" disabled className="bg-[#141418]">Select your industry</option>
                      <option value="Professional Services" className="bg-[#141418]">Professional Services</option>
                      <option value="Contractor & Home Services" className="bg-[#141418]">Contractor &amp; Home Services</option>
                      <option value="Real Estate & Brokerage" className="bg-[#141418]">Real Estate &amp; Brokerage</option>
                      <option value="Other" className="bg-[#141418]">Other</option>
                    </select>
                  </div>

                  {/* Goals */}
                  <div>
                    <label className="block text-xs font-medium text-white/40 mb-1.5 uppercase tracking-wider">
                      Tell us about your goals
                    </label>
                    <textarea
                      name="message"
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      placeholder="What operational challenges are you facing?"
                      rows={4}
                      maxLength={500}
                      className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-sm text-white placeholder-white/20 focus:outline-none focus:border-[#4ADE80]/40 transition-colors resize-none"
                    />
                    <p className="text-xs text-white/25 mt-1 text-right">{message.length}/500</p>
                  </div>

                  <button
                    type="submit"
                    disabled={loading || message.length > 500}
                    className="whitespace-nowrap w-full py-4 rounded-lg bg-[#4ADE80] text-[#09090B] font-bold text-sm hover:bg-[#22C55E] transition-colors duration-200 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? "Submitting..." : "Get Started"}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
